import { useMemo } from 'react';
import { escapeRegExp, sanitizeInput as sanitizeInputUtil } from '../utils/moneyInputUtils';

const CURRENCY_FORMAT_PROBE = 123456.7;

export interface UseCurrencyHelpersOptions {
  locale?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  currency?: string;
}

export interface UseCurrencyHelpersReturn {
  format: (value: number) => string;
  toEditableFromNumber: (value: number) => string;
  sanitizeInput: (input: string) => string;
  parseToNumber: (value: string | number) => number;
  decimalSeparator: string;
  formatter: Intl.NumberFormat;
}

export const useCurrencyHelpers = ({
  locale = 'nl-NL',
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  currency = 'EUR',
}: UseCurrencyHelpersOptions = {}): UseCurrencyHelpersReturn => {
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits,
        maximumFractionDigits,
      }),
    [locale, currency, minimumFractionDigits, maximumFractionDigits]
  );

  const parts = useMemo(() => formatter.formatToParts(CURRENCY_FORMAT_PROBE), [formatter]);

  const decimalSeparator =
    parts.find(p => p.type === 'decimal')?.value ?? '.';

  const groupSeparator =
    parts.find(p => p.type === 'group')?.value ?? ',';

  const currencySymbol =
    parts.find(p => p.type === 'currency')?.value ?? '';

  const parseToNumber = (value: string | number): number => {
    if (typeof value === 'number') return value;
    if (!value || value.trim() === '') return 0;

    const normalized = value
      .replace(new RegExp(escapeRegExp(currencySymbol), 'g'), '')
      .replace(new RegExp(escapeRegExp(groupSeparator), 'g'), '')
      .replace(/\s/g, '')
      .replace(decimalSeparator, '.');

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sanitizeInput = (input: string): string => {
    return sanitizeInputUtil(input, decimalSeparator, groupSeparator, maximumFractionDigits);
  };

  const format = (value: number) => formatter.format(value);

  const toEditableFromNumber = (value: number): string =>
    value
      .toFixed(minimumFractionDigits)
      .replace('.', decimalSeparator);

  return {
    format,
    toEditableFromNumber,
    sanitizeInput,
    parseToNumber,
    decimalSeparator,
    formatter,
  };
};

